import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { localeSchema, type Locale } from '@xphoto/shared';
import { catalogs, resolve, type MessageKey } from './catalogs';
import { pluralize, type PluralKey } from './plural';
import { detectInitialLocale, storeLocale } from './detect';

/** Fonction de traduction : chemin pointé → texte dans la langue courante. */
export type TFunction = (key: MessageKey) => string;

/**
 * Traduction ACCORDÉE EN NOMBRE : choisit la variante `_one`/`_other` selon les
 * règles de la langue courante (`Intl.PluralRules`) et formate `{count}`.
 * Cf. `plural.ts` pour la justification du choix face à la forme « média(s) ».
 */
export type TPluralFunction = (
  key: PluralKey,
  count: number,
  vars?: Record<string, string | number>,
) => string;

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: TFunction;
  tp: TPluralFunction;
  /** Liste ordonnée des langues disponibles (pour le menu). */
  locales: readonly Locale[];
}

const I18nContext = createContext<I18nContextValue | null>(null);

/** Langues proposées, dans l'ordre d'affichage du menu. */
const AVAILABLE_LOCALES = localeSchema.options;

interface I18nProviderProps {
  children: React.ReactNode;
  /** Langue initiale forcée (tests) ; sinon détectée (localStorage → système → en). */
  initialLocale?: Locale;
}

export function I18nProvider({ children, initialLocale }: I18nProviderProps): React.JSX.Element {
  const [locale, setLocaleState] = useState<Locale>(() => initialLocale ?? detectInitialLocale());

  // Reflète la langue sur <html lang> (a11y + SEO) à chaque changement.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    storeLocale(next);
  }, []);

  const t = useCallback<TFunction>((key) => resolve(catalogs[locale], key), [locale]);
  const tp = useCallback<TPluralFunction>(
    (key, count, vars) => pluralize(locale, key, count, vars),
    [locale],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ locale, setLocale, t, tp, locales: AVAILABLE_LOCALES }),
    [locale, setLocale, t, tp],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** Accès au contexte i18n complet (langue courante, setter, `t`, langues). */
export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n doit être utilisé dans un <I18nProvider>');
  return ctx;
}
